import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { trackEvent } from "@/lib/analytics";
import { markQuizReferral } from "@/lib/attribution";
import {
  PATTERN_DEEP_DIVE_URLS,
  SPOT_PATTERN_PUZZLES_BY_ID,
} from "@/lib/spotPatternPuzzles";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ---------- CONTENT POOL ----------
type Round = {
  /** Canonical SpotPatternPuzzle id — see src/lib/spotPatternPuzzles.ts */
  puzzleId: string;
  message: string;
  keyPhrase: string;
  answer: string;
  difficulty: number;
  title: string;
  text: string;
};

const POOL: Round[] = [
  { puzzleId: "darvo-1", message: "I'm sorry you feel that way, but you always do this when I'm trying to explain myself.",
    keyPhrase: "you always do this", answer: "darvo", difficulty: 1,
    title: "That's DARVO.",
    text: "Deny, Attack, Reverse Victim and Offender — the fault gets denied, your reaction gets attacked, and the blame moves onto you, all in one sentence." },
  { puzzleId: "character-attack-1", message: "You're overreacting, like you always do.",
    keyPhrase: "like you always do", answer: "character attack", difficulty: 1,
    title: "That's a character attack.",
    text: "Framing your reaction as a permanent trait — 'like you always do' — makes this about who you are, not about what just happened." },
  { puzzleId: "blame-shifting-1", message: "I only yelled because you pushed me to it.",
    keyPhrase: "because you pushed me to it", answer: "blame shifting", difficulty: 1,
    title: "That's blame-shifting.",
    text: "The behavior gets admitted, then immediately handed to you as its cause — 'you pushed me to it' relocates the responsibility." },
  { puzzleId: "gaslighting-1", message: "Nobody else has ever complained about this. You're the only one who has a problem with it.",
    keyPhrase: "nobody else has ever complained", answer: "gaslighting", difficulty: 2,
    title: "That's gaslighting.",
    text: "A false consensus gets manufactured on the spot — 'nobody else' — to make your own reaction look unreasonable, even irrational." },
  { puzzleId: "isolation-1", message: "I just don't think you should hang out with them anymore. I only want what's best for you.",
    keyPhrase: "hang out with them anymore", answer: "isolation", difficulty: 2,
    title: "That's isolation.",
    text: "Framed as protection, but the actual function is cutting you off from an outside perspective — control wearing the language of care." },
  { puzzleId: "triangulation-1", message: "Even my mom says you're too sensitive about this stuff.",
    keyPhrase: "even my mom says", answer: "triangulation", difficulty: 2,
    title: "That's triangulation.",
    text: "A third person gets pulled in, not for real input, but to add weight against you — this is triangulation, not genuine outside perspective." },
  { puzzleId: "guilt-leveraging-1", message: "I can't believe you'd bring that up now, after everything I've done for you.",
    keyPhrase: "after everything i've done for you", answer: "guilt leveraging", difficulty: 2,
    title: "That's guilt leveraging.",
    text: "Past favors get used as leverage against a present concern — the debt gets called in instead of the actual issue getting addressed." },
  { puzzleId: "gaslighting-2", message: "You're remembering it wrong. That's not what happened at all.",
    keyPhrase: "you're remembering it wrong", answer: "gaslighting", difficulty: 2,
    title: "That's gaslighting.",
    text: "Confidently overwriting your memory, with no actual account of what did happen — that's not disagreement, that's erasure." },
  { puzzleId: "guilt-tripping-1", message: "If you really loved me you'd stop hanging out with your friends so much.",
    keyPhrase: "if you really loved me", answer: "guilt tripping", difficulty: 2,
    title: "That's guilt-tripping.",
    text: "Love gets made conditional on compliance — the ask isn't 'I miss you,' it's 'prove it by giving something up.'" },
  { puzzleId: "minimization-1", message: "Relax, I'm just teasing you. God, you can't take a joke anymore.",
    keyPhrase: "can't take a joke anymore", answer: "minimization", difficulty: 2,
    title: "That's minimization.",
    text: "The 'joke' does the harm, then your reaction to it gets treated as the actual problem — humor used to relocate the blame." },
  { puzzleId: "stonewalling-1", message: "Fine. Whatever you want. I don't even care anymore.",
    keyPhrase: "i don't even care anymore", answer: "stonewalling", difficulty: 3,
    title: "That's stonewalling.",
    text: "This isn't neutrality — withdrawing engagement is being used as punishment, forcing you to chase the conversation to get anywhere." },
  { puzzleId: "love-bombing-1", message: "I've never felt this close to anyone before. You're different from everyone else, I already know you're it for me.",
    keyPhrase: "i already know you're it for me", answer: "love bombing", difficulty: 3,
    title: "That's love bombing.",
    text: "Intensity this early isn't chemistry, it's pace — moving fast enough that normal boundaries and pacing don't get time to form." },
  { puzzleId: "projection-1", message: "You're the one who can't handle criticism. That's always been your problem.",
    keyPhrase: "that's always been your problem", answer: "projection", difficulty: 3,
    title: "That's projection.",
    text: "The trait being accused of is the one the accuser actually struggles with — projection moves the spotlight before it can land on them." },
  { puzzleId: "future-faking-1", message: "I know I messed up. I'm going to change, I promise, you'll see, things are going to be so different now.",
    keyPhrase: "things are going to be so different now", answer: "future faking", difficulty: 3,
    title: "That's future faking.",
    text: "Big promises, no specifics, nothing changed yet. This is the promise that resets the cycle — not evidence the cycle is ending." },
  { puzzleId: "covert-threat-1", message: "I was just joking, but if you're going to make it weird, maybe I can't be myself around you.",
    keyPhrase: "maybe i can't be myself around you", answer: "covert threat", difficulty: 3,
    title: "That's a covert threat.",
    text: "Buried in the sentence is a threat of withdrawal — compliance gets incentivized by the fear of losing the relationship." },
];

const ROUND_TIME = 25000;
const STP_FAQ = [
  {
    "q": "Is this a gaslighting test?",
    "a": "It's a recognition quiz, not a test about your relationship. It trains you to spot manipulation patterns in sample messages. To analyze a message you received, use the free gaslighting detector."
  },
  {
    "q": "Is it free?",
    "a": "Yes. Five rounds a day, no signup."
  },
  {
    "q": "Does it change every day?",
    "a": "Yes. There's a new set of five moments every day."
  },
  {
    "q": "What patterns does it cover?",
    "a": "Gaslighting, guilt-tripping, blame reversal, invalidation, and other manipulation patterns, alongside honest messages that only look like them."
  }
];

const STP_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: STP_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const EPOCH = new Date(2026, 6, 1); // July 1, 2026 — Day #1

function mulberry32(seed: number) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function getDayNumber() {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const e = new Date(EPOCH); e.setHours(0, 0, 0, 0);
  return Math.max(1, Math.floor((today.getTime() - e.getTime()) / 86400000) + 1);
}

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const rand = mulberry32(seed);
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function drawDailyRounds(): Round[] {
  const day = getDayNumber();
  return seededShuffle(POOL, day * 7919).slice(0, 5).sort((a, b) => a.difficulty - b.difficulty);
}

const normWord = (w: string) => (w || "").toLowerCase().replace(/[^a-z0-9']/g, "").replace(/'/g, "");
const normAnswer = (s: string) => (s || "").toLowerCase().replace(/[^a-z]/g, "");

type Token = { raw: string; norm: string; index: number };

function buildTarget(r: Round): { tokens: Token[]; targetSet: Set<number> } {
  const rawWords = r.message.split(/\s+/);
  const tokens: Token[] = rawWords.map((w, i) => ({ raw: w, norm: normWord(w), index: i }));
  const keyWords = r.keyPhrase.split(/\s+/).map(normWord);
  let startIdx = -1;
  for (let i = 0; i <= tokens.length - keyWords.length; i++) {
    let ok = true;
    for (let k = 0; k < keyWords.length; k++) {
      if (tokens[i + k].norm !== keyWords[k]) { ok = false; break; }
    }
    if (ok) { startIdx = i; break; }
  }
  const targetSet = new Set<number>();
  if (startIdx >= 0) for (let k = 0; k < keyWords.length; k++) targetSet.add(startIdx + k);
  return { tokens, targetSet };
}

function renderMaskHTML(answer: string, revealCount: number) {
  let shown = 0;
  let out = "";
  for (const ch of answer) {
    if (ch === " ") { out += "\u00a0\u00a0"; continue; }
    if (shown < revealCount) out += `<b>${ch.toUpperCase()}</b> `;
    else out += "_ ";
    shown++;
  }
  return out;
}

type Screen = "intro" | "quiz" | "result";

const SpotThePattern = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const day = useMemo(() => getDayNumber(), []);
  const [screen, setScreen] = useState<Screen>("intro");
  const [rounds, setRounds] = useState<Round[]>([]);
  const [current, setCurrent] = useState(0);
  const [tiers, setTiers] = useState<number[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  // Persistent per-user daily streak (null = not loaded / anon; number = real)
  const [clarityStreak, setClarityStreak] = useState<number | null>(null);
  const [milestoneHit, setMilestoneHit] = useState<number | null>(null);
  const streakWrittenRef = useRef(false);

  // Display names of the patterns in today's draw (deduped, draw order),
  // used by the "go deeper" links on the result screen.
  const todaysPatterns = useMemo(() => {
    const names: string[] = [];
    for (const r of rounds) {
      const name = SPOT_PATTERN_PUZZLES_BY_ID[r.puzzleId]?.answer;
      if (name && !names.includes(name)) names.push(name);
    }
    return names;
  }, [rounds]);

  const [tokens, setTokens] = useState<Token[]>([]);
  const [targetSet, setTargetSet] = useState<Set<number>>(new Set());
  const [selectedWords, setSelectedWords] = useState<Set<number>>(new Set());
  const [hintsUsed, setHintsUsed] = useState(0);
  const [locked, setLocked] = useState(false);
  const [guess, setGuess] = useState("");
  const [revealTier, setRevealTier] = useState<number | null>(null);
  const [revealTimedOut, setRevealTimedOut] = useState(false);
  const [revealPhraseCorrect, setRevealPhraseCorrect] = useState(false);
  const [revealNameCorrect, setRevealNameCorrect] = useState(false);
  const [timerUrgent, setTimerUrgent] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [challengeName, setChallengeName] = useState("");
  const [copiedMsg, setCopiedMsg] = useState("");

  const timerBarRef = useRef<HTMLDivElement>(null);
  const timeoutRefs = useRef<{ urgent?: number; end?: number }>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lockedRef = useRef(false);

  const currentRound = rounds[current];

  const stopTimer = useCallback(() => {
    if (timeoutRefs.current.urgent) window.clearTimeout(timeoutRefs.current.urgent);
    if (timeoutRefs.current.end) window.clearTimeout(timeoutRefs.current.end);
    timeoutRefs.current = {};
    setTimerRunning(false);
  }, []);

  const submitRound = useCallback((timedOut = false) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    stopTimer();

    const r = rounds[current];
    if (!r) return;
    const nameCorrect = normAnswer(guess) === normAnswer(r.answer) && guess.trim().length > 0;

    let inter = 0;
    selectedWords.forEach((i) => { if (targetSet.has(i)) inter++; });
    const unionSize = new Set([...selectedWords, ...targetSet]).size;
    const phraseCorrect = targetSet.size > 0 && unionSize > 0 && (inter / unionSize) >= 0.55;

    let tier: number;
    if (timedOut && !nameCorrect && selectedWords.size === 0) {
      tier = 0;
    } else {
      const base = (phraseCorrect ? 1 : 0) + (nameCorrect ? 1 : 0);
      if (base === 2 && hintsUsed === 0) tier = 3;
      else if (base === 2 && hintsUsed > 0) tier = 2;
      else if (base === 1) tier = 1;
      else tier = 0;
    }

    setTiers((prev) => [...prev, tier]);
    setTotalPoints((p) => p + tier);
    setStreak((s) => (tier >= 2 ? s + 1 : 0));
    setRevealTier(tier);
    setRevealTimedOut(timedOut);
    setRevealPhraseCorrect(phraseCorrect);
    setRevealNameCorrect(nameCorrect);

    trackEvent("spot_pattern_answer", {
      round: current + 1,
      phraseCorrect,
      nameCorrect,
      hintsUsed,
      timedOut,
      tier,
    });
  }, [current, guess, hintsUsed, rounds, selectedWords, stopTimer, targetSet]);

  const startTimer = useCallback(() => {
    stopTimer();
    setTimerUrgent(false);
    const bar = timerBarRef.current;
    if (bar) {
      bar.style.transition = "none";
      bar.style.transform = "scaleX(1)";
      // reflow
      void bar.offsetWidth;
      bar.style.transition = `transform ${ROUND_TIME}ms linear, background-color .3s`;
      requestAnimationFrame(() => { bar.style.transform = "scaleX(0)"; });
    }
    setTimerRunning(true);
    timeoutRefs.current.urgent = window.setTimeout(() => setTimerUrgent(true), ROUND_TIME * 0.6);
    timeoutRefs.current.end = window.setTimeout(() => {
      if (!lockedRef.current) submitRound(true);
    }, ROUND_TIME);
  }, [stopTimer, submitRound]);

  const renderRoundState = useCallback((idx: number, allRounds: Round[]) => {
    const r = allRounds[idx];
    const { tokens: t, targetSet: ts } = buildTarget(r);
    setTokens(t);
    setTargetSet(ts);
    setSelectedWords(new Set());
    setHintsUsed(0);
    setGuess("");
    setRevealTier(null);
    setLocked(false);
    lockedRef.current = false;
    trackEvent("spot_pattern_round_view", { round: idx + 1 });
    // start timer after paint
    requestAnimationFrame(() => startTimer());
  }, [startTimer]);

  const startQuiz = () => {
    const drawn = drawDailyRounds();
    setRounds(drawn);
    setCurrent(0);
    setTotalPoints(0);
    setTiers([]);
    setStreak(0);
    setScreen("quiz");
    renderRoundState(0, drawn);
  };

  const nextRound = () => {
    const nextIdx = current + 1;
    if (nextIdx >= rounds.length) {
      setScreen("result");
      trackEvent("spot_pattern_complete", { totalScore: totalPoints, tiers });
    } else {
      setCurrent(nextIdx);
      renderRoundState(nextIdx, rounds);
    }
  };

  const toggleWord = (i: number) => {
    if (locked) return;
    setSelectedWords((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const useHint = () => {
    if (locked || !currentRound) return;
    const letterCount = currentRound.answer.replace(/[^a-zA-Z]/g, "").length;
    if (hintsUsed < letterCount - 1) setHintsUsed((h) => h + 1);
  };

  useEffect(() => () => stopTimer(), [stopTimer]);

  // Draw canvas whenever result view or inputs change
  const drawCard = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const W = c.width, H = c.height;
    ctx.fillStyle = "#0a0a0c"; ctx.fillRect(0, 0, W, H);
    const grad = ctx.createRadialGradient(W / 2, 60, 40, W / 2, H / 2, W);
    grad.addColorStop(0, "rgba(63,216,234,0.12)");
    grad.addColorStop(1, "#0a0a0c");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#2a2a30"; ctx.lineWidth = 2; ctx.strokeRect(1, 1, W - 2, H - 2);

    ctx.fillStyle = "#3fd8ea";
    ctx.font = '600 22px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textAlign = "center";
    ctx.fillText("GREYROCKAI  ·  DAY #" + day, W / 2, 90);

    ctx.fillStyle = "#8f8f96";
    ctx.font = '400 20px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.fillText("TODAY'S CLARITY", W / 2, 125);

    ctx.fillStyle = "#3fd8ea";
    ctx.font = '700 170px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.fillText(String(totalPoints), W / 2 - 55, 370);
    ctx.fillStyle = "#8f8f96";
    ctx.font = '700 60px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.fillText("/15", W / 2 + 130, 345);

    const sq = 54, gap = 14, total = tiers.length || 5;
    const rowW = total * sq + (total - 1) * gap;
    const startX = W / 2 - rowW / 2;
    const y = 450;
    const tierColor: Record<number, string> = { 3: "#3fd8ea", 2: "#2ea9b8", 1: "#f2b84e", 0: "#7a8a99" };
    for (let i = 0; i < total; i++) {
      const x = startX + i * (sq + gap);
      ctx.fillStyle = tierColor[tiers[i] !== undefined ? tiers[i] : 0];
      roundRect(ctx, x, y, sq, sq, 8); ctx.fill();
    }

    ctx.fillStyle = "#f2f2f0";
    ctx.font = '700 32px -apple-system, Helvetica, Arial, sans-serif';
    ctx.fillText("Can you beat this?", W / 2, 560);

    const name = challengeName.trim();
    ctx.fillStyle = "#8f8f96";
    ctx.font = '400 23px -apple-system, Helvetica, Arial, sans-serif';
    if (name) ctx.fillText(name + ", today\u2019s set is waiting.", W / 2, 600);
    else ctx.fillText("Same 5 messages for everyone today.", W / 2, 600);

    ctx.fillStyle = "#8f8f96";
    ctx.font = '400 20px -apple-system, Helvetica, Arial, sans-serif';
    ctx.fillText("Tap the phrase. Name the pattern. No list to pick from.", W / 2, 635);

    ctx.fillStyle = "#3fd8ea";
    ctx.font = '600 24px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.fillText("greyrockai.app/spot-the-pattern", W / 2, 780);
  }, [challengeName, day, tiers, totalPoints]);

  useEffect(() => {
    if (screen === "result") drawCard();
  }, [screen, drawCard]);

  // Persist per-user daily streak once the user reaches the result screen.
  // Rules: same day → no change; previous day → +1; otherwise reset to 1.
  // Anon users see nothing (no fake numbers).
  useEffect(() => {
    if (screen !== "result") return;
    if (!user) return;
    if (streakWrittenRef.current) return;
    streakWrittenRef.current = true;

    const todayStr = new Date().toISOString().slice(0, 10);
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("clarity_streak, clarity_last_played_on")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error || !data) return;

      const prev = (data.clarity_streak as number | null) ?? 0;
      const last = data.clarity_last_played_on as string | null;
      let next = 1;
      if (last === todayStr) next = Math.max(prev, 1);
      else if (last) {
        const d = new Date(last + "T00:00:00Z");
        const t = new Date(todayStr + "T00:00:00Z");
        const diffDays = Math.round((t.getTime() - d.getTime()) / 86400000);
        next = diffDays === 1 ? prev + 1 : 1;
      }

      setClarityStreak(next);
      if (next === 7 || next === 30 || next === 100) setMilestoneHit(next);

      if (last !== todayStr || prev !== next) {
        await supabase
          .from("profiles")
          .update({ clarity_streak: next, clarity_last_played_on: todayStr })
          .eq("user_id", user.id);
      }
    })();
  }, [screen, user]);


  const tierGrid = () => {
    const map: Record<number, string> = { 3: "🟦", 2: "🟦", 1: "🟨", 0: "⬛" };
    return tiers.map((t) => map[t]).join("");
  };

  const shareScore = () => {
    const c = canvasRef.current;
    if (!c) return;
    const name = challengeName.trim();
    const grid = tierGrid();
    const callout = name ? `${name}, beat this if you can. ` : `Bet you can't beat this. `;
    const text = `Spot the Pattern — GreyRockAI (Day #${day})\n${totalPoints}/15 ${grid}\n${callout}`;
    const url = window.location.href.split("?")[0];
    trackEvent("spot_pattern_share", { totalScore: totalPoints });
    c.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "spot-the-pattern.png", { type: "image/png" });
      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      if (nav.canShare && nav.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "Spot the Pattern — GreyRockAI", text, url }); return; }
        catch { /* fallthrough */ }
      }
      if (navigator.share) {
        navigator.share({ title: "Spot the Pattern — GreyRockAI", text, url }).catch(() => {});
        return;
      }
      navigator.clipboard.writeText(text + url).then(() => {
        setCopiedMsg("Copied — paste it anywhere.");
        setTimeout(() => setCopiedMsg(""), 2500);
      });
    }, "image/png");
  };

  const downloadCard = () => {
    const c = canvasRef.current;
    if (!c) return;
    const link = document.createElement("a");
    link.download = "spot-the-pattern-day" + day + ".png";
    link.href = c.toDataURL("image/png");
    link.click();
  };

  const goAnalyzer = () => {
    // Client-side navigation never re-runs main.tsx's capture, so persist the
    // quiz referral signals here, before routing.
    markQuizReferral();
    trackEvent("quiz_bridge_cta_clicked", { day });
    navigate("/?utm_source=quiz&utm_medium=organic&utm_campaign=spot_the_pattern");
  };


  const resultTitle = (() => {
    const titles: Record<number, string> = {
      15: "Perfect run. Every phrase, every name, no hints.",
      14: "Nearly flawless.", 13: "Nearly flawless.", 12: "Sharp read.",
      11: "Sharp read.", 10: "Solid instincts.", 9: "Solid instincts.",
      8: "Getting there.", 7: "Getting there.", 6: "Round 3 got you too, huh.",
    };
    return titles[totalPoints] || (totalPoints <= 5 ? "That set was brutal. Try tomorrow's." : "Solid instincts.");
  })();

  const stampLabel = revealTier === null ? "" :
    revealTier === 3 ? "CLEAR SIGHT" :
    revealTier === 2 ? "MOSTLY CLEAR" :
    revealTier === 1 ? "PARTIAL VIEW" :
    revealTimedOut ? "STILL FOGGED" : "FOGGED";

  const letterCount = currentRound ? currentRound.answer.replace(/[^a-zA-Z]/g, "").length : 0;
  const hintDisabled = locked || hintsUsed >= letterCount - 1;

  return (
    <>
      <SEO
        title="Spot the Pattern: Free Daily Gaslighting & Manipulation Quiz | GreyRock AI"
        description="Can you spot gaslighting in a text? Five real-world moments a day. Tell the manipulation from the honest misunderstanding. Free, no signup."
        path="/spot-the-pattern"
        jsonLd={STP_FAQ_JSONLD}
      />
      <style>{`
        .stp-root { --bg:#0a0a0c; --panel:#16161a; --panel-2:#1c1c21; --border:#2a2a30; --text:#f2f2f0; --text-dim:#8f8f96; --accent:#3fd8ea; --amber:#f2b84e; --miss:#f2637d; --fog:#7a8a99; --radius:14px;
          background: radial-gradient(1200px 600px at 50% -10%, rgba(63,216,234,0.08), transparent 60%), var(--bg);
          color:var(--text); font-family:-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
          min-height:100vh; display:flex; align-items:flex-start; justify-content:center; padding:24px 16px 60px; }
        .stp-app { width:100%; max-width:520px; }
        .stp-eyebrow { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; letter-spacing:.14em; color:var(--accent); text-transform:uppercase; text-align:center; margin-bottom:4px; opacity:.9; }
        .stp-h1 { font-family:'Inter', system-ui, sans-serif; font-size:clamp(30px,8.4vw,44px); line-height:1.1; font-weight:700; letter-spacing:-0.025em; color:var(--text); text-align:center; margin:2px 0 16px; }
        .stp-daynum { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:11px; color:var(--text-dim); text-align:center; margin-bottom:18px; letter-spacing:.05em; }
        .stp-roundrow { display:flex; align-items:center; justify-content:center; gap:10px; margin-bottom:14px; }
        .stp-roundcounter { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:13px; color:var(--text-dim); }
        .stp-roundcounter b { color:var(--text); }
        .stp-streak { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; color:var(--accent); transition:.25s; white-space:nowrap; }
        .stp-dots { display:flex; gap:6px; justify-content:center; margin-bottom:16px; }
        .stp-dot { width:7px; height:7px; border-radius:50%; background:var(--border); transition:.3s; }
        .stp-dot.tier3 { background:var(--accent); }
        .stp-dot.tier2 { background:var(--accent); opacity:.6; }
        .stp-dot.tier1 { background:var(--amber); }
        .stp-dot.tier0 { background:var(--fog); }
        .stp-dot.active { background:var(--text-dim); box-shadow:0 0 0 3px rgba(255,255,255,.06); }
        .stp-timerwrap { height:4px; background:var(--border); border-radius:4px; overflow:hidden; margin-bottom:22px; }
        .stp-timerbar { height:100%; width:100%; background:var(--accent); border-radius:4px; transform-origin:left; transition: transform linear, background-color .3s; }
        .stp-timerbar.urgent { background:var(--miss); }
        .stp-lede { font-size:26px; line-height:1.32; font-weight:700; letter-spacing:-0.01em; margin-bottom:20px; }
        .stp-lede .strong { color:var(--text); font-weight:800; }
        .stp-lede .dim { color:var(--text-dim); font-weight:500; }
        .stp-lede .hi { color:var(--accent); font-weight:800; }
        .stp-tag { font-size:14px; color:var(--text-dim); font-style:italic; margin-bottom:26px; padding-left:14px; border-left:2px solid var(--accent); }
        .stp-metarow { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:30px; }
        .stp-metachip { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:11px; letter-spacing:.06em; color:var(--text-dim); border:1px solid var(--border); border-radius:20px; padding:7px 12px; }
        .stp-evidence { background:var(--panel); border:1px solid var(--border); border-radius:var(--radius); padding:20px 18px; margin-bottom:16px; position:relative; }
        .stp-evidence::before { content:"MESSAGE — TAP THE PHRASE"; position:absolute; top:-9px; left:16px; background:var(--bg); padding:0 8px; font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:9px; letter-spacing:.1em; color:var(--text-dim); }
        .stp-wordwrap { font-size:17px; line-height:1.7; font-weight:500; font-style:italic; color:var(--text); }
        .stp-w { cursor:pointer; border-radius:4px; padding:1px 2px; transition:.12s; }
        .stp-w:hover { background:rgba(255,255,255,.06); }
        .stp-w.picked { background:rgba(63,216,234,0.22); color:var(--accent); font-style:normal; }
        .stp-w.locked-correct { background:rgba(63,216,234,0.28); color:var(--accent); font-style:normal; }
        .stp-w.locked-missed { background:rgba(242,99,125,0.18); color:var(--miss); text-decoration: line-through; font-style:normal; }
        .stp-answerblock { margin-top:18px; }
        .stp-prompt { font-size:13.5px; color:var(--text-dim); margin-bottom:8px; }
        .stp-mask { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:15px; letter-spacing:.12em; color:var(--text-dim); margin-bottom:8px; min-height:18px; }
        .stp-mask b { color:var(--accent); }
        .stp-inputrow { display:flex; gap:8px; }
        .stp-nameguess { flex:1; background:var(--panel); border:1px solid var(--border); color:var(--text); padding:13px 14px; border-radius:10px; font-size:15px; font-family:inherit; }
        .stp-nameguess:focus { outline:none; border-color:var(--accent); }
        .stp-hintbtn { background:transparent; border:1px solid var(--border); color:var(--text-dim); padding:0 16px; border-radius:10px; font-size:13px; cursor:pointer; font-family:inherit; white-space:nowrap; }
        .stp-hintbtn:hover:not(:disabled) { border-color:var(--amber); color:var(--amber); }
        .stp-hintbtn:disabled { opacity:.4; cursor:default; }
        .stp-submitbtn { margin-top:14px; width:100%; background:var(--accent); color:#00232a; border:none; padding:14px; border-radius:10px; font-size:15px; font-weight:700; cursor:pointer; font-family:inherit; }
        .stp-submitbtn:hover { filter:brightness(1.08); }
        .stp-submitbtn:disabled { opacity:.5; cursor:default; }
        .stp-reveal { margin-top:18px; padding:20px; border-radius:var(--radius); border:1px solid var(--border); background:var(--panel); animation: stp-stampin .35s cubic-bezier(.2,1.4,.4,1); }
        @keyframes stp-stampin { 0%{opacity:0; transform:scale(1.05) rotate(-2deg);} 60%{opacity:1;} 100%{opacity:1; transform:scale(1) rotate(0);} }
        .stp-stamp { display:inline-block; font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; letter-spacing:.12em; font-weight:700; padding:5px 10px; border-radius:5px; margin-bottom:12px; transform: rotate(-2deg); }
        .stp-stamp.t3 { color:var(--accent); border:1.5px solid var(--accent); }
        .stp-stamp.t2 { color:var(--accent); border:1.5px solid var(--accent); opacity:.75; }
        .stp-stamp.t1 { color:var(--amber); border:1.5px solid var(--amber); }
        .stp-stamp.t0 { color:var(--fog); border:1.5px solid var(--fog); }
        .stp-hintnote { font-size:11.5px; color:var(--text-dim); font-style:italic; margin-top:8px; opacity:.85; }
        .stp-reveal h3 { font-size:17px; margin-bottom:8px; }
        .stp-reveal p { color:var(--text-dim); font-size:14.5px; line-height:1.55; margin-bottom:6px; }
        .stp-reveal .pts { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; color:var(--text-dim); margin-top:10px; }
        .stp-nextbtn { margin-top:16px; width:100%; background:var(--accent); color:#00232a; border:none; padding:14px; border-radius:10px; font-size:15px; font-weight:700; cursor:pointer; font-family:inherit; }
        .stp-nextbtn:hover { filter:brightness(1.08); }
        .stp-result { text-align:center; }
        .stp-scorewrap { margin: 6px 0 20px; }
        .stp-scoreline { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:13px; letter-spacing:.1em; color:var(--text-dim); text-transform:uppercase; margin-bottom:6px; }
        .stp-bignum { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:64px; font-weight:700; color:var(--accent); line-height:1; }
        .stp-bignum span { color:var(--text-dim); font-size:32px; }
        .stp-result h2 { font-size:22px; margin-bottom:6px; font-weight:800; }
        .stp-result .sub { color:var(--text-dim); font-size:14px; margin-bottom:22px; }
        .stp-cardwrap { display:flex; justify-content:center; margin-bottom:20px; }
        .stp-canvas { width:100%; max-width:340px; border-radius:16px; border:1px solid var(--border); display:block; }
        .stp-nameinput { width:100%; background:var(--panel); border:1px solid var(--border); color:var(--text); padding:12px 14px; border-radius:10px; font-size:14px; margin-bottom:12px; font-family:inherit; }
        .stp-nameinput::placeholder { color:var(--text-dim); }
        .stp-actions { display:flex; flex-direction:column; gap:10px; }
        .stp-sharebtn { background:var(--accent); color:#00232a; border:none; padding:15px; border-radius:10px; font-size:15px; font-weight:700; cursor:pointer; font-family:inherit; }
        .stp-dlbtn { background:transparent; color:var(--text); border:1px solid var(--border); padding:13px; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; }
        .stp-dlbtn:hover { border-color:var(--accent); color:var(--accent); }
        .stp-bridgebtn { background:transparent; color:var(--text); border:1px solid var(--border); padding:15px; border-radius:10px; font-size:15px; font-weight:600; cursor:pointer; font-family:inherit; }
        .stp-bridgebtn:hover { border-color:var(--accent); color:var(--accent); }
        .stp-comeback { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; color:var(--text-dim); margin-top:16px; }
        .stp-comeback b { color:var(--accent); }
        .stp-streakline { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:13px; letter-spacing:.06em; color:var(--text-dim); text-align:center; margin: 10px 0 6px; }
        .stp-streakline b { color:var(--accent); font-weight:700; }
        .stp-milestone { margin-top:6px; font-family:-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, sans-serif; font-style:italic; font-size:13px; color:var(--text); opacity:.9; letter-spacing:0; }
        .stp-replaybtn { background:transparent; color:var(--text-dim); border:none; padding:6px; font-size:12px; cursor:pointer; font-family:inherit; text-decoration:underline; margin-top:6px; }
        .stp-copied { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:12px; color:var(--accent); text-align:center; margin-top:10px; height:14px; }
        .stp-startbtn { width:100%; background:var(--accent); color:#00232a; border:none; padding:16px; border-radius:10px; font-size:16px; font-weight:700; cursor:pointer; font-family:inherit; }
        .stp-startbtn:hover { filter:brightness(1.08); }
        .stp-backrow { margin-bottom: 10px; }
        .stp-backbtn { background: transparent; border: none; color: var(--text-dim); font-family: inherit; font-size: 13px; cursor: pointer; padding: 0; display: inline-flex; align-items: center; gap: 6px; transition: color .2s; }
        .stp-backbtn:hover { color: var(--text); }
        .stp-footer { text-align:center; margin-top:34px; font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:11px; color:var(--text-dim); letter-spacing:.06em; }
        .stp-deeper { margin-top: 24px; border-top: 1px solid var(--border); padding-top: 16px; text-align: left; }
        .stp-deeperhead { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size:11px; letter-spacing:.1em; color:var(--text-dim); text-transform:uppercase; margin-bottom:10px; }
        .stp-deeperlist { list-style:none; margin:0; padding:0; }
        .stp-deeperlist li { font-size:14px; line-height:1.7; color:var(--text-dim); }
        .stp-deeperlist a { color:var(--accent); text-decoration:none; }
        .stp-deeperlist a:hover { text-decoration:underline; }
        .stp-seo { margin-top:40px; border-top:1px solid var(--border); padding-top:8px; text-align:left; }
        .stp-seo h2 { font-size:19px; font-weight:700; color:var(--text); margin:28px 0 10px; letter-spacing:-.01em; }
        .stp-seo h3 { font-size:15px; font-weight:600; color:var(--text); margin:18px 0 6px; }
        .stp-seo p, .stp-seo li { font-size:15px; line-height:1.7; color:var(--text-dim); }
        .stp-seo ul { padding-left:20px; list-style:disc; margin:0 0 10px; }
        .stp-seo-cta { display:inline-block; margin-top:14px; border:1px solid var(--border); color:var(--text); padding:13px 18px; border-radius:10px; font-size:14px; font-weight:600; text-decoration:none; }
        .stp-seo-cta:hover { border-color:var(--accent); color:var(--accent); }
        @media (prefers-reduced-motion: reduce) { .stp-reveal { animation:none; } .stp-timerbar { transition:none; } }
      `}</style>

      <div className="stp-root">
        <div className="stp-app">
          <div className="stp-backrow">
            <button className="stp-backbtn" onClick={() => navigate("/")}>← Back to GreyRock AI</button>
          </div>
          <div className="stp-eyebrow">GreyRockAI</div>
          <div className="stp-daynum">DAY #{day}</div>

          {screen === "intro" && (
            <div>
              <h1 className="stp-h1">Spot the Pattern: Free Daily Gaslighting Quiz</h1>
              <h2 className="stp-lede">
                <span className="strong">Five moments.</span>{" "}
                <span className="dim">Some are clear. Some are fogged by something — fear, memory, shame, doubt.</span>{" "}
                <span className="hi">Can you tell which is which?</span>
              </h2>
              <div className="stp-tag">The fog doesn't come from them. It comes from all of it.</div>
              <div className="stp-metarow">
                <div className="stp-metachip">5 ROUNDS</div>
                <div className="stp-metachip">TAP + TYPE</div>
                <div className="stp-metachip">NO SIGNUP</div>
              </div>
              <button className="stp-startbtn" onClick={startQuiz}>Start today's set &nbsp;→</button>
              <section className="stp-seo">
                <h2>How to spot gaslighting in a message</h2>
                <p>Each day's set shows you five short moments from real-feeling conversations. Some are manipulation: gaslighting, guilt-tripping, blame reversal. Some are just people being clumsy, tired, or honest. Your job is to tell which is which. It's practice for the moment when it's your own phone.</p>
                <h2>What you're looking for</h2>
                <ul>
                  <li>Reality denial: "I never said that." "That never happened."</li>
                  <li>Memory questioning: "You're remembering it wrong."</li>
                  <li>Reaction reframing: "You're way too sensitive."</li>
                  <li>Blame reversal: "You twist everything I say."</li>
                </ul>
                <p>One line isn't proof. A pattern is: the same moves, repeated, about things you know happened.</p>
                <h2>Have a real message you're unsure about?</h2>
                <p>This quiz uses sample messages. To check a text you actually received, use the free gaslighting detector. It runs in your browser, and your message is never sent to a server.</p>
                <Link to="/free-gaslighting-detector" className="stp-seo-cta">Check a real message →</Link>
                <h2>FAQ</h2>
                {STP_FAQ.map((f) => (
                  <div key={f.q}>
                    <h3>{f.q}</h3>
                    <p>{f.a}</p>
                  </div>
                ))}
              </section>
            </div>
          )}

          {screen === "quiz" && currentRound && (
            <div>
              <div className="stp-roundrow">
                <div className="stp-roundcounter">ROUND <b>{current + 1}</b> / {rounds.length}</div>
                {streak >= 2 && <div className="stp-streak">🔥 {streak} IN A ROW</div>}
              </div>
              <div className="stp-dots">
                {rounds.map((_, i) => {
                  const cls = ["stp-dot"];
                  if (i < tiers.length) cls.push("tier" + tiers[i]);
                  else if (i === current) cls.push("active");
                  return <div key={i} className={cls.join(" ")} />;
                })}
              </div>
              <div className="stp-timerwrap">
                <div ref={timerBarRef} className={"stp-timerbar" + (timerUrgent ? " urgent" : "")} />
              </div>

              <div className="stp-evidence">
                <div className="stp-wordwrap">
                  {tokens.map((tok) => {
                    const cls = ["stp-w"];
                    if (locked) {
                      if (targetSet.has(tok.index)) cls.push("locked-correct");
                      else if (selectedWords.has(tok.index)) cls.push("locked-missed");
                    } else if (selectedWords.has(tok.index)) cls.push("picked");
                    return (
                      <span key={tok.index}>
                        <span className={cls.join(" ")} onClick={() => toggleWord(tok.index)}>{tok.raw}</span>{" "}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="stp-answerblock">
                <div className="stp-prompt">Name the pattern:</div>
                <div className="stp-mask" dangerouslySetInnerHTML={{ __html: renderMaskHTML(currentRound.answer, hintsUsed) }} />
                <div className="stp-inputrow">
                  <input
                    className="stp-nameguess"
                    type="text"
                    placeholder="Type your answer..."
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={guess}
                    disabled={locked}
                    onChange={(e) => setGuess(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !locked) submitRound(false); }}
                  />
                  <button className="stp-hintbtn" onClick={useHint} disabled={hintDisabled}>Focus</button>
                </div>
                {current === 0 && hintsUsed === 0 && !locked && (
                  <div className="stp-hintnote">Bring it into focus — costs clarity.</div>
                )}
                <button className="stp-submitbtn" onClick={() => submitRound(false)} disabled={locked}>Lock it in</button>
              </div>

              {revealTier !== null && (
                <div className="stp-reveal">
                  <div className={"stp-stamp t" + revealTier}>{stampLabel}</div>
                  <h3>{currentRound.title}</h3>
                  <p>{currentRound.text}</p>
                  <p className="pts">
                    +{revealTier} pts   ·   phrase {revealPhraseCorrect ? "✓" : "✗"}   ·   name {revealNameCorrect ? "✓" : "✗"}
                    {hintsUsed ? `   ·   ${hintsUsed} hint${hintsUsed > 1 ? "s" : ""} used` : ""}
                  </p>
                  <button className="stp-nextbtn" onClick={nextRound}>
                    {current === rounds.length - 1 ? "See your result →" : "Next round →"}
                  </button>
                </div>
              )}
            </div>
          )}

          {screen === "result" && (
            <div className="stp-result">
              <div className="stp-scorewrap">
                <div className="stp-scoreline">Today's clarity</div>
                <div className="stp-bignum">{totalPoints}<span>/15</span></div>
              </div>
              <h2>{resultTitle}</h2>
              <div className="sub">Clear sight / Mostly clear / Partial view / Fogged, per round.</div>
              {clarityStreak !== null && clarityStreak > 0 && (
                <div className="stp-streakline">
                  Clarity Streak: <b>{clarityStreak}</b>
                  {milestoneHit && (
                    <div className="stp-milestone">{milestoneHit} days of clearer thinking.</div>
                  )}
                </div>
              )}

              <div className="stp-cardwrap">
                <canvas ref={canvasRef} className="stp-canvas" width={680} height={850} />
              </div>

              <input
                className="stp-nameinput"
                type="text"
                placeholder="Dare a friend by name (optional)"
                maxLength={24}
                value={challengeName}
                onChange={(e) => setChallengeName(e.target.value)}
              />

              <div className="stp-actions">
                <button className="stp-sharebtn" onClick={shareScore}>Share this card</button>
                <button className="stp-dlbtn" onClick={downloadCard}>Download image</button>
                <button className="stp-bridgebtn" onClick={goAnalyzer}>You just saw it clearly in a stranger's words. Try it on your own →</button>
              </div>
              <div className="stp-copied">{copiedMsg}</div>
              {todaysPatterns.length > 0 && (
                <div className="stp-deeper">
                  <div className="stp-deeperhead">Today's patterns</div>
                  <ul className="stp-deeperlist">
                    {todaysPatterns.map((name) => {
                      const url = PATTERN_DEEP_DIVE_URLS[name];
                      return (
                        <li key={name}>
                          {url ? (
                            <Link to={url}>Want the full breakdown on {name}? →</Link>
                          ) : (
                            <span>{name}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div className="stp-comeback">Day <b>#{day}</b>. <br />A new fog to see through tomorrow.</div>
              <button className="stp-replaybtn" onClick={startQuiz}>Replay today's set</button>
            </div>
          )}

          <footer className="stp-footer">GREYROCKAI — FORENSIC PATTERN RECOGNITION</footer>
        </div>
      </div>
    </>
  );
};

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath(); ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default SpotThePattern;
